import React from "react";

import type { Record } from "~/tools/record";

import type { Api, KubernetesLogHistogramBucket, KubernetesLogSeverityHistogramBucket } from "../../../common/openapi";
import { logSeverityToColor } from "../../../generic/log-severity";
import { Group, Rect } from "../../_canvas";

import type { CommonTimelineBucketProps } from "./domain";
import { TimelineEventBucketsContainer } from "./TimelineEventBucketsContainer";

const SeverityWeights: Record<Api.LogSeverity, number> = {
	ERROR: 0,
	WARNING: 1,
	OTHER: 2,
};

const sortBySeverity = (
	{ severity: a }: KubernetesLogSeverityHistogramBucket,
	{ severity: b }: KubernetesLogSeverityHistogramBucket,
) => {
	const severityWeightA = SeverityWeights[a];
	const severityWeightB = SeverityWeights[b];
	return severityWeightA - severityWeightB;
};

export type TimelineEventBucketsWithLogLevelsProps = CommonTimelineBucketProps<KubernetesLogHistogramBucket>;

export const TimelineEventBucketsWithLogLevels: React.FC<TimelineEventBucketsWithLogLevelsProps> = (props) => (
	<TimelineEventBucketsContainer
		{...props}
		getBucketEndTime={({ endTime }) => endTime}
		getBucketStartTime={({ startTime }) => startTime}
		listening={false}
	>
		{({ bucket, x, width, height }, scaleCount) => (
			<BucketWithLogLevels
				height={height}
				logLevels={bucket.logSeverityBuckets
					.filter(({ count }) => count > 0)
					.map(({ severity, count }) => ({ severity, count: scaleCount(count) }))
					.sort(sortBySeverity)}
				width={width}
				x={x}
			/>
		)}
	</TimelineEventBucketsContainer>
);

const BucketWithLogLevels: React.FC<{
	logLevels: ReadonlyArray<KubernetesLogSeverityHistogramBucket>;
	x: number;
	width: number;
	height: number;
}> = ({ logLevels, x, width, height }) => {
	const levels = logLevels.reduce(
		(acc, { severity, count }) => {
			const prev = acc.at(-1);
			const shift = prev == null ? 0 : prev.shift + prev.count;

			return [...acc, { severity, count, shift }];
		},
		[] as Array<KubernetesLogSeverityHistogramBucket & { shift: number }>,
	);

	return (
		<Group x={x} zIndex={2}>
			{levels.map(({ severity, shift, count }) => (
				<Rect
					fill={logSeverityToColor(severity)}
					height={count}
					key={severity}
					width={width}
					y={height - shift - count}
				/>
			))}
		</Group>
	);
};
